import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  InputAdornment,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
} from '@mui/material';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { useFormik } from 'formik';
import * as Yup from 'yup';

const API_URL = '';

/* Validation Schema */
const validationSchema = Yup.object({
  organization_name: Yup.string().min(2, 'Minimo 2 caracteres').required('La organizacion es requerida'),
  username: Yup.string()
    .min(3, 'Mínimo 3 caracteres')
    .required('El nombre de usuario es requerido'),
  email: Yup.string()
    .email('Ingresa un email válido')
    .required('El email es requerido'),
  password: Yup.string()
    .min(12, 'Minimo 12 caracteres')
    .required('La contraseña es requerida'),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref('password'), null], 'Las contraseñas deben coincidir')
    .required('Confirma tu contraseña'),
});

/* ─── Animated Background (Shared with Login) ────────────── */
const AnimatedBackground = () => (
  <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', zIndex: 0 }}>
    <Box sx={{
      position: 'absolute', inset: 0,
      backgroundImage: `
        linear-gradient(rgba(111,255,233,0.03) 1px, transparent 1px),
        linear-gradient(90deg, rgba(111,255,233,0.03) 1px, transparent 1px)
      `,
      backgroundSize: '50px 50px',
    }} />
    <Box sx={{
      position: 'absolute',
      width: '300px', height: '300px',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(111,255,233,0.1) 0%, transparent 70%)',
      top: '10%', left: '10%',
      animation: 'float 8s infinite alternate',
    }} />
    <Box sx={{
      position: 'absolute',
      width: '400px', height: '400px',
      borderRadius: '50%',
      background: 'radial-gradient(circle, rgba(91,192,190,0.08) 0%, transparent 70%)',
      bottom: '10%', right: '10%',
      animation: 'float 10s infinite alternate-reverse',
    }} />
    <style>{`
      @keyframes float {
        from { transform: translate(0, 0); }
        to { transform: translate(20px, 40px); }
      }
    `}</style>
  </Box>
);

const inputSx = {
  mb: 2,
  '& .MuiOutlinedInput-root': {
    color: '#fff',
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    '& fieldset': { borderColor: 'rgba(111,255,233,0.2)' },
    '&:hover fieldset': { borderColor: 'rgba(111,255,233,0.5)' },
    '&.Mui-focused fieldset': { borderColor: '#6FFFE9' },
  },
  '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.5)' },
  '& .MuiInputLabel-root.Mui-focused': { color: '#6FFFE9' },
};

const Signup = ({ onSignup, switchToLogin }) => {
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: '', severity: 'error' });

  const formik = useFormik({
    initialValues: {
      organization_name: '',
      username: '',
      email: '',
      password: '',
      confirmPassword: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const res = await fetch(`${API_URL}/v1/api/signup`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            organization_name: values.organization_name,
            username: values.username,
            email: values.email,
            password: values.password,
          }),
        });
        const data = await res.json().catch(() => ({}));

        if (!res.ok) throw new Error(data.message || (res.status === 503 ? 'El registro no esta disponible en este momento.' : 'No se pudo crear la cuenta. Intenta nuevamente.'));

        setSnack({ open: true, message: 'Cuenta creada con éxito. Inicia sesión.', severity: 'success' });
        setTimeout(() => {
          onSignup(data);
          switchToLogin();
        }, 1500);
      } catch (err) {
        setSnack({ open: true, message: err.message, severity: 'error' });
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <Box sx={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: '#0B132B',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <AnimatedBackground />

      <Box sx={{
        position: 'relative',
        zIndex: 1,
        width: '100%',
        maxWidth: 450,
        p: 4,
        background: 'rgba(28, 37, 65, 0.7)',
        backdropFilter: 'blur(20px)',
        borderRadius: '24px',
        border: '1px solid rgba(111,255,233,0.1)',
        boxShadow: '0 25px 50px rgba(0,0,0,0.3)',
      }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box component="img" src={`${process.env.PUBLIC_URL || ''}/iso.png`} alt="Absolluty" sx={{ width: 56, height: 56, objectFit: 'contain', display: 'block', mx: 'auto', mb: 1 }} />
          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', mb: 1 }}>
            Crear Cuenta
          </Typography>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
            Únete a la plataforma de videovigilancia AI
          </Typography>
        </Box>

        <form onSubmit={formik.handleSubmit}>
          <TextField fullWidth name="organization_name" label="Organizacion" value={formik.values.organization_name}
            onChange={formik.handleChange} onBlur={formik.handleBlur}
            error={formik.touched.organization_name && Boolean(formik.errors.organization_name)}
            helperText={formik.touched.organization_name && formik.errors.organization_name} sx={inputSx} />
          <TextField
            fullWidth
            name="username"
            label="Nombre de Usuario"
            value={formik.values.username}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.username && Boolean(formik.errors.username)}
            helperText={formik.touched.username && formik.errors.username}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <PersonOutlineIcon sx={{ color: 'rgba(111,255,233,0.5)' }} />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          <TextField
            fullWidth
            name="email"
            label="Correo Electrónico"
            value={formik.values.email}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.email && Boolean(formik.errors.email)}
            helperText={formik.touched.email && formik.errors.email}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <EmailOutlinedIcon sx={{ color: 'rgba(111,255,233,0.5)' }} />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          <TextField
            fullWidth
            name="password"
            label="Contraseña"
            type={showPwd ? 'text' : 'password'}
            value={formik.values.password}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.password && Boolean(formik.errors.password)}
            helperText={formik.touched.password && formik.errors.password}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: 'rgba(111,255,233,0.5)' }} />
                </InputAdornment>
              ),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPwd(!showPwd)} edge="end" sx={{ color: 'rgba(255,255,255,0.5)' }}>
                    {showPwd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          <TextField
            fullWidth
            name="confirmPassword"
            label="Confirmar Contraseña"
            type={showPwd ? 'text' : 'password'}
            value={formik.values.confirmPassword}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            error={formik.touched.confirmPassword && Boolean(formik.errors.confirmPassword)}
            helperText={formik.touched.confirmPassword && formik.errors.confirmPassword}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <LockOutlinedIcon sx={{ color: 'rgba(111,255,233,0.5)' }} />
                </InputAdornment>
              ),
            }}
            sx={inputSx}
          />

          <Button
            type="submit"
            fullWidth
            disabled={loading}
            sx={{
              mt: 2,
              py: 1.5,
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #6FFFE9 0%, #5BC0BE 100%)',
              color: '#0B132B',
              fontWeight: 700,
              '&:hover': { background: '#5BC0BE' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Registrarse'}
          </Button>
        </form>

        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography sx={{ color: 'rgba(255,255,255,0.6)', fontSize: '0.9rem' }}>
            ¿Ya tienes una cuenta?{' '}
            <Typography
              component="span"
              onClick={switchToLogin}
              sx={{ color: '#6FFFE9', cursor: 'pointer', fontWeight: 600, '&:hover': { textDecoration: 'underline' } }}
            >
              Inicia sesión aquí
            </Typography>
          </Typography>
        </Box>
      </Box>

      <Snackbar
        open={snack.open}
        autoHideDuration={6000}
        onClose={() => setSnack({ ...snack, open: false })}
      >
        <Alert severity={snack.severity} sx={{ width: '100%' }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Signup;
